import { Link } from "react-router";
import { useQueries } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCheck,
  FileCheck2,
  Flag,
  type LucideIcon,
  MessageSquareWarning,
  Scale,
  Sparkles,
  UserCheck,
  Wallet,
} from "lucide-react";
import type { Permission } from "@poji/shared";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface Queue {
  key: string;
  title: string;
  hint: string;
  /** menüdeki ikonla aynı: kart ile ekran birbirini tanıtsın */
  icon: LucideIcon;
  to: string;
  permission: Permission;
  path: string;
  pick: (r: Record<string, unknown[]>) => unknown[];
}

/** İş kutuları: bekleyen işler yetkiye göre (K-097). Sayılar ilk 100 kayıttan. */
const QUEUES: Queue[] = [
  {
    key: "providers",
    icon: UserCheck,
    title: "Başvuru",
    hint: "incelemede hizmet veren",
    to: "/providers",
    permission: "providers.applications.review",
    path: "/v1/admin/providers?status=pending_review",
    pick: (r) => r.providers ?? [],
  },
  {
    key: "documents",
    icon: FileCheck2,
    title: "Belge",
    hint: "doğrulama bekliyor",
    to: "/documents",
    permission: "providers.verification.manage",
    path: "/v1/admin/documents?status=pending&limit=100",
    pick: (r) => r.documents ?? [],
  },
  {
    key: "disputes",
    icon: Scale,
    title: "İtiraz",
    hint: "açık",
    to: "/disputes",
    permission: "tasks.disputes.manage",
    path: "/v1/admin/disputes?status=open",
    pick: (r) => r.disputes ?? [],
  },
  {
    key: "approvals",
    icon: CheckCheck,
    title: "Onay",
    hint: "dört göz bekliyor",
    to: "/approvals",
    permission: "staff.approvals.decide",
    path: "/v1/admin/approvals",
    pick: (r) => r.approvals ?? [],
  },
  {
    key: "reports",
    icon: Flag,
    title: "Bildirim",
    hint: "açık içerik bildirimi",
    to: "/reports",
    permission: "support.reports.moderate",
    path: "/v1/admin/reports?status=open&limit=100",
    pick: (r) => r.reports ?? [],
  },
  {
    key: "messages",
    icon: MessageSquareWarning,
    title: "Mesaj",
    hint: "işaretlenmiş",
    to: "/messages",
    permission: "support.reports.moderate",
    path: "/v1/admin/messages/flagged",
    pick: (r) => r.messages ?? [],
  },
  {
    key: "service-requests",
    icon: Sparkles,
    title: "Hizmet talebi",
    hint: "yeni",
    to: "/service-requests",
    permission: "catalog.services.manage",
    path: "/v1/admin/service-requests?status=new&limit=100",
    pick: (r) => r.requests ?? [],
  },
  {
    key: "payouts",
    icon: Wallet,
    title: "Takılan ödeme",
    hint: "Stripe hatası",
    to: "/payouts",
    permission: "accounting.ledger.view",
    path: "/v1/admin/payouts?view=stuck&limit=100",
    pick: (r) => r.payouts ?? [],
  },
];

export function DashboardPage() {
  const { can } = useAuth();
  const visible = QUEUES.filter((q) => can(q.permission));
  const results = useQueries({
    queries: visible.map((q) => ({
      queryKey: ["queue", q.key],
      queryFn: () => api<Record<string, unknown[]>>(q.path),
      refetchInterval: 60_000,
    })),
  });

  return (
    <>
      <PageHeader
        title="Genel bakış"
        description="Ekibin bekleyen işleri. Sayılar dakikada bir yenilenir."
      />
      {visible.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Hesabınıza henüz bir rol atanmamış. Bir süper yöneticiden yetki isteyin.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {visible.map((q, i) => {
            const r = results[i]!;
            const n = r.data ? q.pick(r.data).length : null;
            return (
              <Link key={q.key} to={q.to} className="group">
                <Card className="h-full transition-colors group-hover:border-primary/40">
                  <CardHeader className="flex flex-row items-center justify-between gap-2 pb-1">
                    <div className="text-sm text-muted-foreground">{q.title}</div>
                    <div
                      aria-hidden
                      className={cn(
                        "grid size-9 shrink-0 place-items-center rounded-lg transition-colors",
                        n ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      <q.icon className="size-[18px]" />
                    </div>
                  </CardHeader>
                  <CardContent className="flex items-end justify-between">
                    <div>
                      {r.isError ? (
                        <div className="text-sm text-destructive">yüklenemedi</div>
                      ) : n === null ? (
                        <Skeleton className="h-8 w-12" />
                      ) : (
                        <div className="text-3xl font-semibold tabular">
                          {n >= 100 ? "100+" : n}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">{q.hint}</div>
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
